import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, VolumeX, X, Settings as SettingsIcon, Users, User, BellOff, Bell, UserCheck, Square, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import {
  SoundboardSound,
  SoundboardPlayMode,
  SoundboardUser,
  subscribeToSounds,
  subscribeToSoundboardUsers,
  subscribeToActiveAudioCount,
  triggerSound,
  triggerStop,
  playSoundLocally,
  stopAllSounds,
  getStoredVolume,
  setStoredVolume,
  getStoredMuted,
  setStoredMuted,
  getStoredPlayMode,
  setStoredPlayMode,
  getStoredDnd,
  setStoredDnd,
  getStoredTargetUserId,
  setStoredTargetUserId,
} from '../../services/soundboardService';
import { Link } from 'react-router-dom';
import { HolidayFloaterButton } from '../holiday/HolidayFloaterButton';

/**
 * Floating top-left launcher button. Renders only for users with
 * `soundboardAccess === true` (or Master Admin). When opened, expands
 * a horizontal bar to the RIGHT of the button with one button per uploaded
 * sound. Clicking any button broadcasts a trigger to all soundboard users.
 */
export const SoundboardLauncher: React.FC<{ leftOffset?: number }> = ({ leftOffset = 16 }) => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [sounds, setSounds] = useState<SoundboardSound[]>([]);
  const [muted, setMuted] = useState(getStoredMuted());
  const [volume, setVolume] = useState(getStoredVolume());
  const [playMode, setPlayMode] = useState<SoundboardPlayMode>(getStoredPlayMode());
  const [dnd, setDnd] = useState(getStoredDnd());
  const [targetUserId, setTargetUserId] = useState<string | null>(getStoredTargetUserId());
  const [soundboardUsers, setSoundboardUsers] = useState<SoundboardUser[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeAudioCount, setActiveAudioCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMasterAdmin = !!currentUser?.roles?.includes(UserRole.MasterAdmin);
  const hasAccess = !!currentUser?.soundboardAccess || isMasterAdmin;

  // Subscribe to the sound list whenever the user has access.
  useEffect(() => {
    if (!hasAccess) return;
    const unsub = subscribeToSounds(setSounds, () => setSounds([]));
    return () => unsub();
  }, [hasAccess]);

  // Persist volume / mute changes locally (per-device, not per-user-doc).
  useEffect(() => setStoredVolume(volume), [volume]);
  useEffect(() => setStoredMuted(muted), [muted]);
  useEffect(() => setStoredPlayMode(playMode), [playMode]);
  useEffect(() => setStoredDnd(dnd), [dnd]);
  useEffect(() => setStoredTargetUserId(targetUserId), [targetUserId]);

  // Subscribe to potential targets (only when the user mode is selected,
  // to avoid spinning up extra listeners by default).
  useEffect(() => {
    if (!hasAccess || playMode !== 'user') return;
    const unsub = subscribeToSoundboardUsers(
      (list) => setSoundboardUsers(list),
      () => setSoundboardUsers([]),
    );
    return () => unsub();
  }, [hasAccess, playMode]);

  // Track how many sounds are currently playing on this device so the Stop
  // button can be disabled when there's nothing to stop.
  useEffect(() => {
    return subscribeToActiveAudioCount(setActiveAudioCount);
  }, []);

  // Close the bar when clicking outside it or pressing Esc.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClick = async (sound: SoundboardSound) => {
    if (!currentUser) return;
    if (playMode === 'user' && !targetUserId) {
      toast.error('Pick a user first');
      return;
    }
    setBusyId(sound.id);
    try {
      if (playMode === 'me') {
        // Local-only — no Firestore write, no broadcast.
        playSoundLocally(sound);
      } else {
        const target = playMode === 'user' ? targetUserId : null;
        // Debug: confirm the target is actually being attached to the trigger.
        console.log(
          `[Soundboard] sending sound="${sound.name}" mode=${playMode} targetUserId=${target ?? 'null'} senderUid=${currentUser.uid}`,
        );
        await triggerSound(
          sound,
          currentUser.uid,
          currentUser.displayName || currentUser.email || 'Unknown',
          target,
        );
      }
    } catch (err) {
      console.error('Failed to trigger sound:', err);
      toast.error('Could not play sound');
    } finally {
      // Brief debounce so the button visibly pulses; nothing actually
      // blocks rapid retriggers.
      setTimeout(() => setBusyId(null), 250);
    }
  };

  const targetUser = useMemo(
    () => (targetUserId ? soundboardUsers.find((u) => u.uid === targetUserId) : null),
    [targetUserId, soundboardUsers],
  );

  const userModeReady = playMode !== 'user' || !!targetUserId;

  /**
   * Stop button — what gets paused depends on the active play-mode toggle:
   *  • all  → broadcast a stop to every soundboard user
   *  • me   → only this device
   *  • user → only the chosen recipient
   * Local audio is always paused immediately so the press feels instant.
   */
  const handleStop = async () => {
    if (!currentUser) return;
    stopAllSounds();
    if (playMode === 'me') return;
    if (playMode === 'user' && !targetUserId) {
      toast.error('Pick a user first');
      return;
    }
    try {
      await triggerStop(
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown',
        playMode === 'user' ? targetUserId : null,
      );
    } catch (err) {
      console.error('Failed to broadcast stop:', err);
      toast.error('Could not broadcast stop');
    }
  };

  // Stop is enabled when:
  //  • something is playing locally, OR
  //  • the toggle would broadcast a stop to other devices.
  const stopEnabled =
    activeAudioCount > 0 ||
    (playMode === 'all') ||
    (playMode === 'user' && !!targetUserId);

  const sortedSounds = useMemo(
    () =>
      [...sounds]
        // Automation-only sounds are reserved for auto-event triggers and
        // never appear as manual launcher buttons.
        .filter((s) => !s.automationOnly)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [sounds],
  );

  const visibleSounds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedSounds;
    return sortedSounds.filter((s) => {
      return (
        s.name.toLowerCase().includes(q) ||
        (s.emoji && s.emoji.toLowerCase().includes(q))
      );
    });
  }, [sortedSounds, search]);

  const saveSound = useMemo(
    () => sounds.find((s) => s.name.trim().toLowerCase() === 'save') || null,
    [sounds],
  );

  const handleQuickSave = async () => {
    if (!currentUser || !saveSound) return;
    setBusyId(saveSound.id);
    try {
      await triggerSound(saveSound, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown', null);
    } catch (err) {
      console.error('Failed to trigger SAVE sound:', err);
      toast.error('Could not play SAVE');
    } finally {
      setTimeout(() => setBusyId(null), 250);
    }
  };

  if (!currentUser || !hasAccess) return null;

  return (
    <div
      ref={containerRef}
      className="fixed top-2 z-[60] flex items-start gap-1.5"
      style={{ left: `${leftOffset}px` }}
    >
      {/* Launcher button — small and tucked in the corner. */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-center w-5 h-5 rounded-full shadow-md border transition-all ${
          open
            ? 'bg-emerald-500 hover:bg-emerald-600 border-emerald-400 text-white'
            : dnd
              ? 'bg-amber-600/90 hover:bg-amber-700 border-amber-500 text-white'
              : 'bg-gray-900/95 hover:bg-gray-800 border-gray-700 text-gray-100'
        }`}
        title={
          open
            ? 'Close soundboard'
            : dnd
              ? 'Soundboard (Do Not Disturb on)'
              : 'Open soundboard'
        }
        aria-label="Soundboard"
      >
        {open ? (
          <X className="w-3 h-3" />
        ) : dnd ? (
          <BellOff className="w-2.5 h-2.5" />
        ) : (
          <Volume2 className="w-2.5 h-2.5" />
        )}
      </button>

      {isMasterAdmin && saveSound && !open && (
        <button
          onClick={handleQuickSave}
          disabled={busyId === saveSound.id}
          className="flex items-center justify-center h-5 px-2 rounded-full shadow-md border bg-gray-900/95 hover:bg-gray-800 border-gray-700 text-gray-100 text-[9px] font-bold tracking-wide transition-all disabled:bg-emerald-500 disabled:border-emerald-400"
          title="Play SAVE for everyone with soundboard access"
          aria-label="Play SAVE for everyone"
        >
          SAVE
        </button>
      )}

      {!open && <HolidayFloaterButton />}

      {/* Sound panel — controls row on top, sounds wrap below. */}
      {open && (
        <div className="flex flex-col gap-2 bg-gray-900/95 backdrop-blur-sm text-white rounded-xl px-3 py-2 shadow-lg border border-gray-700 max-w-[min(78vw,640px)]">
          {/* Controls row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Volume + mute */}
            <button
              onClick={() => setMuted((m) => !m)}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              title={muted ? 'Unmute (you won\u2019t hear sounds)' : 'Mute (only your speakers)'}
            >
              {muted ? (
                <VolumeX className="w-4 h-4 text-amber-400" />
              ) : (
                <Volume2 className="w-4 h-4 text-gray-200" />
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 h-1 accent-emerald-400"
              title="Volume"
            />

            {/* Play target toggle: All vs User vs Me */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-full p-0.5 ml-1">
              <button
                onClick={() => setPlayMode('all')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
                  playMode === 'all'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
                title="Play for everyone with soundboard access"
              >
                <Users className="w-3 h-3" />
                All
              </button>
              <button
                onClick={() => setPlayMode('user')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
                  playMode === 'user'
                    ? 'bg-violet-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
                title="Send to a specific user only"
              >
                <UserCheck className="w-3 h-3" />
                User
              </button>
              <button
                onClick={() => setPlayMode('me')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
                  playMode === 'me'
                    ? 'bg-sky-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
                title="Play only on your own speakers"
              >
                <User className="w-3 h-3" />
                Me
              </button>
            </div>

            {/* Target user picker — only shown when in 'user' mode. */}
            {playMode === 'user' && (
              <select
                value={targetUserId ?? ''}
                onChange={(e) => setTargetUserId(e.target.value || null)}
                className={`bg-white/5 border rounded-full px-2 py-1 text-[10px] font-semibold focus:outline-none focus:ring-2 focus:ring-violet-400 max-w-[160px] truncate ${
                  targetUserId
                    ? 'border-violet-400/40 text-violet-100'
                    : 'border-amber-400/40 text-amber-200 animate-pulse'
                }`}
                title={
                  targetUser
                    ? `Sending to ${targetUser.displayName || targetUser.email}`
                    : 'Pick a user to send sounds to'
                }
              >
                <option value="" className="bg-gray-900 text-gray-200">
                  {soundboardUsers.length === 0 ? 'Loading users…' : '— Pick user —'}
                </option>
                {soundboardUsers
                  .filter((u) => u.uid !== currentUser?.uid)
                  .map((u) => (
                    <option key={u.uid} value={u.uid} className="bg-gray-900 text-gray-200">
                      {u.displayName || u.email}
                      {u.isMasterAdmin ? ' (Admin)' : ''}
                    </option>
                  ))}
              </select>
            )}

            {/* Do Not Disturb — silences incoming sounds (broadcasts + event
                triggers) but lets your own "Me" plays still work. */}
            <button
              onClick={() => setDnd((d) => !d)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold transition-colors border ${
                dnd
                  ? 'bg-amber-500/20 border-amber-400/40 text-amber-200 hover:bg-amber-500/30'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              title={
                dnd
                  ? 'Do Not Disturb is ON — incoming sounds are silenced. Click to allow them again.'
                  : 'Click to silence incoming sounds (Do Not Disturb)'
              }
            >
              {dnd ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
              DND
            </button>

            {/* Stop — kills sound playback. Scope follows the All/Me/User
                toggle: broadcast to all, just me, or just the targeted user.
                Local audio stops instantly regardless of mode. */}
            <button
              onClick={handleStop}
              disabled={!stopEnabled}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold transition-colors border ${
                stopEnabled
                  ? activeAudioCount > 0
                    ? 'bg-red-500/20 border-red-400/40 text-red-200 hover:bg-red-500/30 animate-pulse'
                    : 'bg-white/5 border-red-400/20 text-red-200 hover:bg-red-500/20'
                  : 'bg-white/5 border-white/10 text-gray-500 cursor-not-allowed'
              }`}
              title={
                !stopEnabled
                  ? 'Nothing is playing'
                  : playMode === 'me'
                    ? `Stop ${activeAudioCount === 1 ? 'the sound' : `${activeAudioCount} sounds`} playing on your speakers`
                    : playMode === 'user'
                      ? `Stop sounds on ${targetUser?.displayName || targetUser?.email || 'selected user'}\u2019s device`
                      : 'Stop sounds on everyone\u2019s devices'
              }
            >
              <Square className="w-3 h-3" />
              Stop
            </button>

            {/* Master Admin shortcut to settings */}
            {isMasterAdmin && (
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="ml-auto p-1.5 rounded-full hover:bg-white/10 transition-colors"
                title="Manage sounds"
              >
                <SettingsIcon className="w-3.5 h-3.5 text-gray-300" />
              </Link>
            )}
          </div>

          {/* Search — filters the library by name or emoji. Only shown
              when there are enough sounds to make scanning useful. */}
          {sortedSounds.length > 6 && (
            <div className="relative">
              <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sounds…"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-7 pr-7 py-1 text-[11px] text-white placeholder:text-gray-500 focus:outline-none focus:border-emerald-400/40"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-white/10"
                  title="Clear search"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
          )}

          {/* Sound list — wraps onto multiple rows so every sound is visible. */}
          {sortedSounds.length === 0 ? (
            <div className="flex items-center gap-2 px-1 py-1 text-[11px] text-gray-400">
              <span>No sounds yet</span>
              {isMasterAdmin && (
                <Link
                  to="/settings"
                  className="text-emerald-400 hover:text-emerald-300 underline"
                  onClick={() => setOpen(false)}
                >
                  Add some
                </Link>
              )}
            </div>
          ) : visibleSounds.length === 0 ? (
            <div className="px-1 py-1 text-[11px] text-gray-400">
              No sounds match &quot;{search}&quot;
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 border-t border-white/10 pt-2">
              {visibleSounds.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleClick(s)}
                  disabled={busyId === s.id || !userModeReady}
                  className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all ${
                    busyId === s.id
                      ? 'bg-emerald-500/30 ring-2 ring-emerald-400 scale-95'
                      : !userModeReady
                        ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                        : 'bg-white/10 hover:bg-white/20 active:scale-95'
                  }`}
                  title={
                    !userModeReady
                      ? 'Pick a user above first'
                      : playMode === 'me'
                        ? `Play "${s.name}" on your speakers only`
                        : playMode === 'user'
                          ? `Send "${s.name}" to ${targetUser?.displayName || targetUser?.email || 'selected user'}`
                          : `Play "${s.name}" for everyone`
                  }
                >
                  {s.emoji ? (
                    <span className="text-base leading-none">{s.emoji}</span>
                  ) : (
                    <Volume2 className="w-3 h-3 text-gray-300" />
                  )}
                  <span className="truncate max-w-[140px]">{s.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SoundboardLauncher;
